import React from 'react'
import { Helmet } from 'react-helmet'
import AppLayoutTitle from './appLayoutTitle'


const AppExchange = () => {
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text)
    }

    return <>
        <Helmet>
            <title>Online Exchange | Sparkling Email</title>
        </Helmet>
        <>
            <AppLayoutTitle title="Microsoft Exchange Online Integration" />
            <div className="px-3 py-3">
                <p>
                    This guide covers permission grants, connector setup, mail flow rule creation, and verification
                    steps. Follow each section to ensure secure, reliable translation and signature services without
                    installing any client‑side software.
                </p>
                <p>
                    Follow these steps to connect Sparkling Email with your Exchange Online tenant. You’ll create
                    two connectors in the Exchange Admin Center, set up a mail‑flow rule, and verify that everything
                    routes through our Sparkling Email servers.
                </p>

                <h5>Step 1. Create Outbound Connector</h5>
                <ol>
                    <li>Log in to the Exchange Admin Center and go to <strong>Mail flow &gt; Connectors</strong>.</li>
                    <li>Click <strong>+ Add a connector</strong>, select <em>From: Microsoft 365</em> and <em>To: Partner organization</em>.</li>
                    <li>Enter:
                        <ul>
                            <li><strong>Name:</strong> To Sparkling Email</li>
                            <li><strong>Description:</strong> Route outbound mail through Sparkling Email</li>
                            <li>Enable <em>Use this connector only when specified in a rule</em> and <em>Require TLS</em>.</li>
                        </ul>
                    </li>
                </ol>
                <div className='text-center my-4'>
                    <img
                        className="img-fluid border border-1 rounded-2"
                        src="/images/dashboard/sparkling-email-01-outbound-connector-settings.png"
                        alt="Outbound connector settings"
                        width="80%"
                    />
                    <div className="fs-8 text-muted mt-1">Outbound connector settings</div>
                </div>
                <hr className="my-4" />
                <h5>Step 2. Create Mail Flow Rule</h5>
                <ol>
                    <li>In <strong>Mail flow &gt; Rules</strong>, click <strong>+ Add a rule</strong> and choose <em>Create a new rule</em>.</li>
                    <li>Set:
                        <ul>
                            <li><strong>Name:</strong> Route via Sparkling Email</li>
                            <li><strong>Apply this rule if…</strong> your desired criteria (e.g. sender, recipient, keywords).</li>
                            <li><strong>Do the following…</strong> Route the message using the “To Sparkling Email” connector.</li>
                            <li>Check <em>Stop processing more rules</em>.</li>
                        </ul>
                    </li>
                </ol>
                <div className='text-center my-4'>
                    <img
                        className="img-fluid border border-1 rounded-2"
                        src="/images/dashboard/sparkling-email-02-mail-flow-rule-setup.png"
                        alt="Mail flow rule setup"
                        width="80%"
                    />
                    <div className="fs-8 text-muted mt-1">Mail flow rule setup</div>
                </div>
                <hr className="my-4" />
                <h5>Step 3. Create Inbound Connector</h5>
                <ol>
                    <li>Back in <strong>Mail flow &gt; Connectors</strong>, click <strong>+ Add a connector</strong> again.</li>
                    <li>Select <em>From: Partner organization</em> and <em>To: Microsoft 365</em>.</li>
                    <li>Enter:
                        <ul>
                            <li><strong>Name:</strong> From Sparkling Email</li>
                            <li>Enable <em>Only accept email from specified IP addresses</em> and add our server IPs.</li>
                            <li>Require TLS.</li>
                        </ul>
                    </li>
                </ol>
                <div className='text-center my-4'>
                    <img
                        className="img-fluid border border-1 rounded-2"
                        src="/images/dashboard/sparkling-email-03-inbound-connector-settings.png"
                        alt="Inbound connector settings"
                        width="80%"
                    />
                    <div className="fs-8 text-muted mt-1">Inbound connector settings</div>
                </div>
                <hr className="my-4" />

                <h5>Step 4. Server Addresses &amp; Ports</h5>
                <p>
                    Use the following Sparkling Email SMTP endpoints on port 25. Click “Copy” to add them directly
                    into your connector or mail‑flow rule.
                </p>

                {[
                    {
                        region: "North America",
                        list: [
                            { label: "US East (New York)", value: "us-east1.sparklingemail.com", isRecommended: true },
                            { label: "US East (New York)", value: "us-east2.sparklingemail.com", isRecommended: true },
                            { label: "Canada (Montreal)", value: "ca-11.sparklingemail.com" },
                            { label: "Canada (Montreal)", value: "ca-12.sparklingemail.com" },
                            { label: "Canada (Toronto)", value: "ca-13.sparklingemail.com" },
                            { label: "Canada (Toronto)", value: "ca-14.sparklingemail.com" },
                        ],
                    },
                    {
                        region: "Europe",
                        list: [
                            { label: "Germany (Munich)", value: "eu-11.sparklingemail.com" },
                            { label: "Germany (Munich)", value: "eu-12.sparklingemail.com" },
                        ],
                    },
                ].map((group, idx) => (
                    <div className="container mb-4" key={group.region}>
                        <strong className="row mb-3">{group.region}</strong>
                        {group.list.map((srv, i) => (
                            <div className="row mb-3" key={i}>
                                <div className="col-6">
                                    <div className="input-group">
                                        <label className="form-label mt-2 me-3" htmlFor={`server${idx}${i}`}>
                                            {srv.label}
                                            {srv.isRecommended &&
                                                <span title="Recommended"><i className="fas fa-check-circle text-success ms-2"></i></span>
                                            }:
                                        </label>
                                        <input
                                            name={`server${idx}${i}`}
                                            id={`server${idx}${i}`}
                                            type="text"
                                            className="form-control"
                                            value={srv.value}
                                            disabled
                                            readOnly />
                                        <button
                                            type="button"
                                            className={"btn " + (srv.isRecommended ? "btn-success" : "btn-outline-secondary")}
                                            onClick={() => copyToClipboard(srv.value)}
                                        >
                                            <i className="far fa-copy"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}

                <p>
                    After setting up both connectors and the mail‑flow rule, send a test email to confirm that
                    messages are routed through Sparkling Email. Check your Sparkling Email dashboard for logs
                    of the test message.
                </p>
            </div>
        </>
    </>
}
export default AppExchange